// ─── L'anagrafica e il borsellino ────────────────────────────────────────────
//
// 🔑 L'IDENTITÀ È `id`, E NIENT'ALTRO. Nel sistema di oggi il socio È il suo
// telefono (`local_key = 'phone:39…'` per 2.798 su 2.826): chi cambia numero
// cambia chiave, e ciò che era legato a lui si scollega. Qui il telefono è un
// attributo unico fra i vivi, e basta.
// ⛔ `external_ref` — la chiave di origine — non compare in nessuno schema di
// questo file, e non è una dimenticanza: esiste solo perché una seconda corsa
// del travaso ritrovi la stessa persona. Il giorno in cui una schermata lo
// mostra, qualcuno comincerà a usarlo per riconoscere qualcuno.
//
// 💶 Il denaro sta in CENTESIMI interi, sempre. La virgola la mette solo
// `formatCents`, all'ultimo momento, davanti agli occhi di chi legge.
import { z } from 'zod';
import { dataSchema } from './circolo.js';
import { chiaveIdempotenzaSchema } from './money.js';
import { metodoDiCassaSchema } from './cassa.js';
import { tipoBookingSchema } from './calendario.js';
// ── Il socio ────────────────────────────────────────────────────────────────
/** Il vincolo del database, lo stesso della colonna `phone_e164`. */
const TELEFONO = /^\+[1-9][0-9]{6,17}$/;
/**
 * Il telefono in forma internazionale.
 *
 * ⚖️ La forma si impone in SCRITTURA, non in lettura: è il vincolo del
 * database (`^\+[1-9][0-9]{6,17}$`) portato fin qui, così chi digita legge una
 * frase in italiano invece di una violazione di `check`. E senza questa
 * severità l'unicità confronterebbe «333 123 4567» con «+393331234567» come
 * fossero due persone — che è esattamente il doppione che il travaso conta.
 */
export const telefonoSchema = z
    .string()
    // 📞 Voce 25: il numero si scrive come viene, e si normalizza PRIMA del
    // controllo, con la stessa funzione del travaso e del bot (⇒ `normalizzaTelefono`).
    // Quando non sa, si lascia com'è scritto: il controllo lo rifiuta in italiano.
    .transform((scritto) => normalizzaTelefono(scritto) ?? scritto.trim())
    .pipe(z
    .string()
    .regex(TELEFONO, 'Il telefono non sembra un numero: controlla che ci siano solo cifre, almeno sei.'));
/**
 * Il livello di gioco, scritto a mano dalla segreteria.
 *
 * ⛔ Non arriva da nessuna autovalutazione: quella è fuori perimetro (§ README.1.2).
 * `numeric(3,1)` in colonna ⇒ un decimale, e niente di più fine.
 */
export const livelloSchema = z.number().min(0).max(99.9);
/**
 * 🏷️ **Il soprannome** (24/09/2026, dal mockup approvato): come l'assistente
 * su Telegram chiama il socio. Lo sceglie il socio dal bot, la segreteria lo
 * vede e lo corregge dalla scheda. Vuoto ⇒ l'assistente lo chiama per nome.
 *
 * 📏 Venti caratteri, solo lettere, spazi e apostrofi: è un nome con cui
 * chiamare una persona, e un numero o un link lì dentro finirebbe in ogni
 * risposta dell'assistente. ⚠️ Lo vedono il socio, l'assistente e la
 * segreteria: gli altri soci vedono sempre nome e cognome.
 */
export const soprannomeSchema = z
    .string()
    .trim()
    .min(1, 'Il soprannome è vuoto')
    .max(20, 'Il soprannome può avere al massimo 20 caratteri')
    .regex(/^\p{L}[\p{L}' ’]*$/u, 'Il soprannome può avere solo lettere, spazi e apostrofi');
export const socioSchema = z.object({
    id: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    /** ⇒ `soprannomeSchema`. */
    nickname: z.string().nullable(),
    /** Generato dal DATABASE, non qui: due posti che compongono il nome lo compongono diverso. */
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    gender: z.enum(['M', 'F']).nullable(),
    birthDate: dataSchema.nullable(),
    level: z.number().nullable(),
    levelNote: z.string().nullable(),
    notes: z.string().nullable(),
    /** Dati morbidi su cui nessuno interroga: giorni e fasce preferite, tipo di partita. */
    preferences: z.record(z.string(), z.unknown()),
    active: z.boolean(),
    /** Valorizzata ⇒ archiviato: la riga resta, esce dagli elenchi e dalle unicità. */
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    /**
     * 🆕 28/09/2026 (Partite Aperte): almeno una prova del test del livello
     * consegnata. Con `level` dice se per il bot il test è «fatto» (scelta 1A).
     */
    hasAssessment: z.boolean().default(false),
});
/**
 * La riga dell'elenco: **meno** campi del socio intero, e di proposito.
 *
 * ⚖️ Su ~2.800 soci la lista si sfoglia mentre si digita: ogni campo in più è
 * peso che attraversa la rete a ogni battuta. La scheda piena si chiede quando
 * qualcuno la apre.
 */
export const socioInElencoSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    level: z.number().nullable(),
    active: z.boolean(),
    archived: z.boolean(),
    /** 💶 `null` = nessun movimento, che NON è «saldo zero verificato». */
    walletBalanceCents: z.number().int().nullable(),
});
export const elencoSociSchema = z.object({
    items: z.array(socioInElencoSchema),
    /**
     * Quanti ce ne sono in tutto per questa ricerca.
     *
     * ⚠️ `null` quando non si è contato — e si conta solo alla prima pagina:
     * un `count(*)` su ogni battuta di tastiera è la cosa che fa sembrare lenta
     * una ricerca che è veloce.
     */
    total: z.number().int().nullable(),
    /** L'ultimo elemento di questa pagina, da rimandare indietro per la prossima. */
    nextCursor: z.string().nullable(),
});
/**
 * 🚨 `z.coerce` sui numeri della querystring: da lì arriva SEMPRE una stringa,
 * e uno schema che si aspetta un numero rifiuta `?limit=50` senza dire perché.
 */
export const domandaSociSchema = z.object({
    q: z.string().trim().max(120).optional(),
    /** `true` = solo gli attivi (il caso normale), `false` = solo i disattivati. */
    active: z.stringbool().optional(),
    /** Gli archiviati stanno FUORI se non si chiedono: sono usciti dal circolo. */
    archived: z.stringbool().default(false),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: z.string().max(200).optional(),
});
const nomeSchema = z.string().trim().min(1, 'Serve il nome').max(80);
export const creaSocioSchema = z.object({
    firstName: nomeSchema,
    lastName: z.string().trim().min(1, 'Serve il cognome').max(80),
    email: z.email('L’indirizzo email non è scritto bene').max(200).nullable().default(null),
    phone: telefonoSchema.nullable().default(null),
    gender: z.enum(['M', 'F']).nullable().default(null),
    birthDate: dataSchema.nullable().default(null),
    level: livelloSchema.nullable().default(null),
    levelNote: z.string().trim().max(400).nullable().default(null),
    notes: z.string().trim().max(2000).nullable().default(null),
});
/**
 * ⛔ Scritto per esteso, NON con `.partial()`.
 *
 * ⚖️ `.partial()` renderebbe opzionale anche ciò che non deve esserlo, e — la
 * parte che costa — non distingue «non tocco questo campo» da «voglio
 * svuotarlo»: `null` è un valore legittimo per email, telefono e livello.
 * Scrivendolo a mano, `undefined` vuol dire «lascia com'è» e `null` vuol dire
 * «cancella», e le due cose sono leggibili.
 */
export const modificaSocioSchema = z.object({
    firstName: nomeSchema.optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    nickname: soprannomeSchema.nullable().optional(),
    email: z.email('L’indirizzo email non è scritto bene').max(200).nullable().optional(),
    phone: telefonoSchema.nullable().optional(),
    gender: z.enum(['M', 'F']).nullable().optional(),
    birthDate: dataSchema.nullable().optional(),
    level: livelloSchema.nullable().optional(),
    levelNote: z.string().trim().max(400).nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    active: z.boolean().optional(),
});
// ── Il borsellino ───────────────────────────────────────────────────────────
export const VERSI_BORSELLINO = ['recharge', 'charge', 'refund'];
/** Come si dicono in italiano, in un posto solo. */
export const ETICHETTE_VERSO = {
    recharge: 'Ricarica',
    charge: 'Uscita',
    refund: 'Rimborso',
};
/** `+1` mette dentro, `-1` toglie. È la stessa regola della vista `member_wallet_balance`. */
export function segnoDelVerso(v) {
    return v === 'charge' ? -1 : 1;
}
export const movimentoBorsellinoSchema = z.object({
    id: z.uuid(),
    direction: z.enum(VERSI_BORSELLINO),
    /** ⭐ SEMPRE POSITIVO: il verso lo dà `direction`. Due verità sullo stesso segno divergono. */
    amountCents: z.number().int().positive(),
    reason: z.string().nullable(),
    /** L'incasso da cui nasce, quando ce n'è uno (Fase 6). */
    paymentId: z.uuid().nullable(),
    /** 🌙 Valorizzato ⇒ è un credito che scade (dalla caparra), e questo è il giorno. */
    expiresOn: z.string().nullable().optional(),
    /**
     * ⭐ Valorizzato ⇒ questa riga **È lo storno** di quella, e non si storna a
     * sua volta. 🗣️ *«Fallo uguale nel borsellino»* (18/09/2026): è la gemella di
     * `voidsPaymentId` in cassa, e nasce dallo stesso difetto — prima il legame
     * viveva solo in una frase del motivo, e uno storno si poteva stornare
     * all'infinito.
     */
    reversesTransactionId: z.uuid().nullable(),
    /** Valorizzato ⇒ questa riga **è stata stornata** da quella. */
    reversedByTransactionId: z.uuid().nullable(),
    recordedAt: z.string(),
    recordedByEmail: z.string().nullable(),
});
export const borsellinoSchema = z.object({
    memberId: z.uuid(),
    /**
     * 💶 Viene dalla VISTA, non da una colonna: il saldo si calcola dai
     * movimenti. Una colonna che qualcuno aggiorna è una colonna che prima o poi
     * mente, e quando mente nessuno se ne accorge perché è l'unica cosa che si
     * guarda.
     */
    balanceCents: z.number().int(),
    transactions: z.array(movimentoBorsellinoSchema),
    /**
     * 🌙 Il credito che scade (dalle caparre, 25/09/2026), per data di scadenza.
     * Il saldo lo comprende già: questo dice solo DI CHE denaro è fatto.
     */
    credits: z.array(z.object({ amountCents: z.number().int(), expiresOn: z.string() })).optional(),
});
export const ricaricaSchema = z.object({
    amountCents: z.number().int().positive('La ricarica dev’essere positiva'),
    reason: z.string().trim().max(200).nullable().default(null),
    /**
     * 💶 CON CHE MEZZO IL DENARO È PASSATO DAL CASSETTO — e questa riga è quella
     * che fa quadrare la giornata (Fase 6).
     *
     * ⚖️ Una ricarica da 50,00 € in contanti mette 50,00 € nel cassetto: se
     * restasse solo un movimento del borsellino, a fine serata la segreteria
     * conterebbe 50,00 € che il riepilogo non sa spiegare. ⇒ Con un mezzo qui, la
     * ricarica genera **anche** la sua riga di cassa, nella stessa transazione.
     * ⛔ `null` = rettifica: un aggiustamento che NON tocca il cassetto (un bonus,
     *    la correzione di un travaso). È lecito, ma è una scelta da fare apposta —
     *    per questo il valore non ha un valore predefinito comodo.
     */
    method: metodoDiCassaSchema.nullable().default(null),
    idempotencyKey: chiaveIdempotenzaSchema,
});
/**
 * Togliere denaro dal borsellino: il socio se lo riprende.
 *
 * 🔄 **Il § 02.5 la chiamava `/refund`, e il nome era ambiguo.** Nella vista
 * del saldo `refund` è un verso che **aggiunge** (Fase 6: un incasso col
 * borsellino che viene stornato ci rimette dentro i soldi). ⇒ Chiamare
 * `/refund` la rotta che TOGLIE avrebbe fatto sì che la stessa parola volesse
 * dire due cose opposte a due metri di distanza.
 * 📌 Un nome che descrive il gesto da una parte e il suo contrario dall'altra
 *    non è un dettaglio di stile: è un errore che aspetta.
 */
export const prelievoSchema = z.object({
    amountCents: z.number().int().positive('L’uscita dev’essere positiva'),
    reason: z.string().trim().max(200).nullable().default(null),
    /**
     * 💶 Come sopra, al contrario: restituire 50,00 € in contanti li TOGLIE dal
     * cassetto, e in cassa è una riga negativa. Senza, il riepilogo direbbe che
     * quei contanti ci sono ancora.
     */
    method: metodoDiCassaSchema.nullable().default(null),
    idempotencyKey: chiaveIdempotenzaSchema,
});
/**
 * Lo storno di un movimento: ne scrive uno **uguale e contrario**.
 *
 * ⛔ L'importo NON si passa: si prende dal movimento che si sta stornando. Se
 * lo scrivesse chi chiama, uno storno potrebbe valere più dell'originale — e
 * il borsellino guadagnerebbe denaro da un gesto che si chiama «annulla».
 */
export const stornoBorsellinoSchema = z.object({
    transactionId: z.uuid(),
    reason: z.string().trim().max(200).nullable().default(null),
});
// ── La scheda del socio ─────────────────────────────────────────────────────
export const rigaAttivitaSchema = z.object({
    bookingId: z.uuid(),
    date: dataSchema,
    time: z.string(),
    durationMinutes: z.number().int(),
    courtName: z.string(),
    /* ⚖️ Stretto a `tipoBookingSchema` e non più `z.string()`: la scheda del socio
       scrive il TIPO al posto del titolo (17/09/2026), e per scriverlo in italiano
       serve sapere che è uno dei cinque. Una stringa qualunque non lo garantiva. */
    type: tipoBookingSchema,
    title: z.string().nullable(),
    status: z.string(),
    /** Quanto gli è stato messo a carico su quella prenotazione. */
    amountDueCents: z.number().int().nullable(),
});
export const rigaIncassoSchema = z.object({
    id: z.uuid(),
    businessDate: dataSchema,
    method: z.string(),
    amountCents: z.number().int(),
    status: z.string(),
    recordedAt: z.string(),
});
export const attivitaSocioSchema = z.object({
    bookings: z.array(rigaAttivitaSchema),
    payments: z.array(rigaIncassoSchema),
});
export const domandaAttivitaSchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
// ── L'export ────────────────────────────────────────────────────────────────
export const domandaExportSchema = z.object({
    format: z.enum(['csv', 'xlsx']).default('csv'),
    q: z.string().trim().max(120).optional(),
    active: z.stringbool().optional(),
    archived: z.stringbool().default(false),
});
// ── L'import dalla rubrica Google ───────────────────────────────────────────
export const contattoTrovatoSchema = z.object({
    displayName: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    /**
     * Cosa succederebbe a questo contatto:
     *   · `nuovo`     — non c'è nessun socio con questo telefono o questa email;
     *   · `aggiorna`  — c'è, e qualche campo cambierebbe;
     *   · `uguale`    — c'è, e non cambierebbe niente;
     *   · `scartato`  — manca il nome, o il telefono non è normalizzabile.
     */
    esito: z.enum(['nuovo', 'aggiorna', 'uguale', 'scartato']),
    /** Il socio che questo contatto tocca, quando ce n'è uno. */
    memberId: z.uuid().nullable(),
    perché: z.string().nullable(),
});
/**
 * 🚨 L'ANTEPRIMA VIENE PRIMA DELLA SCRITTURA, ED È IL PUNTO DELLA ROTTA.
 *
 * ⚖️ Una rubrica personale contiene il dentista e il meccanico. Riversarla
 * nell'anagrafica del circolo senza guardare vuol dire trovarsi centinaia di
 * non-soci il giorno dopo, e nessuno saprà più quali righe erano lì prima.
 * ⇒ `apply: false` (il difetto) **legge e basta**; `apply: true` scrive, e
 *   scrive SOLO i contatti che chi ha guardato ha scelto.
 */
export const domandaImportSchema = z.object({
    apply: z.boolean().default(false),
    /** Gli indirizzi da scrivere davvero. Vuoto con `apply: true` = non si scrive niente. */
    scelti: z.array(z.string()).default([]),
});
export const esitoImportSchema = z.object({
    /** `false` ⇒ è stata solo un'anteprima: niente è stato scritto. */
    applicato: z.boolean(),
    contatti: z.array(contattoTrovatoSchema),
    creati: z.number().int(),
    aggiornati: z.number().int(),
});
// ── Le due funzioni pure che il travaso e l'API DEVONO condividere ──────────
//
// 🚨 Stanno qui, e non in ciascuno dei due, perché normalizzano la chiave su
// cui poggia l'unicità dei soci. Due copie che divergono di un carattere fanno
// entrare la stessa persona due volte a seconda della porta da cui arriva — il
// travaso o l'import della rubrica — e nessuno se ne accorge finché non ci
// sono due «Mario Rossi» con lo stesso numero scritto in due modi.
/**
 * Porta un numero alla forma `+39…`.
 *
 * 🔄 **È L'UNICA regola del telefono**, e dal 25/09/2026 la usano anche i
 * moduli della scheda (voce 25) e l'importazione (voce 26). La voce 25 ne
 * aveva scritta una seconda, `ripulisciTelefono`, che divergeva su due casi (il
 * fisso senza prefisso, e un numero estero scritto senza «+»). È stata tolta:
 * due regole sono la stessa persona che entra due volte da due porte.
 *
 * 🚨 Torna `null` quando NON SA, e non prova a indovinare. Un numero
 * normalizzato male è peggio di un numero mancante: collide con quello di
 * un'altra persona sul vincolo di unicità, e le due righe diventano una.
 *
 * 📏 Misurato sulla sorgente il 15/09/2026 su 2.826 soci: 2.776 già `+39`
 * puliti, 45 con un altro prefisso internazionale, 4 vuoti, 1 di altra forma.
 * ⇒ Il caso da non sbagliare non è la massa: è quell'uno.
 */
export function normalizzaTelefono(grezzo) {
    if (grezzo == null)
        return null;
    // Spazi, punti, trattini, parentesi e lo spazio unificatore che arriva
    // incollato dai fogli di calcolo.
    let pulito = String(grezzo).replace(/[\s\u00a0.\-()/]/g, '');
    if (pulito === '')
        return null;
    // 📏 **Il prefisso DOPPIO di Matchpoint** (voce 26, misurato il 25/09/2026 sul
    // suo export: 12 righe su 1.132). Scrive «+39 » davanti a un numero che il
    // prefisso ce l'ha già: «+39 +393331234567», «+39 +41791234567». Il numero
    // vero è quello dopo l'ULTIMO «+».
    const ultimoPiu = pulito.lastIndexOf('+');
    if (ultimoPiu > 0)
        pulito = pulito.slice(ultimoPiu);
    let cifre;
    if (pulito.startsWith('+')) {
        cifre = pulito.slice(1);
    }
    else if (pulito.startsWith('00')) {
        cifre = pulito.slice(2);
    }
    else if (/^3[0-9]{8,9}$/.test(pulito)) {
        // Un cellulare italiano scritto senza prefisso: è il caso di gran lunga
        // più comune fra quelli da sistemare — perché il circolo è in Italia e i
        // fissi non cominciano per 3.
        cifre = `39${pulito}`;
    }
    else if (/^0[0-9]{5,10}$/.test(pulito)) {
        // 📞 Voce 25: un fisso italiano senza prefisso («02 1234 5678»). In Italia
        // i fissi cominciano per 0, e lo 0 resta anche col prefisso: +39 02….
        cifre = `39${pulito}`;
    }
    else {
        // Senza «+», senza «00», e non comincia né per 3 né per 0: è un numero
        // che il prefisso ce l'ha già, scritto senza «+» («393331234567»,
        // «41791234567»). Aggiungere +39 qui lo sbaglierebbe.
        cifre = pulito;
    }
    if (!/^[1-9][0-9]{6,17}$/.test(cifre))
        return null;
    return `+${cifre}`;
}
/**
 * Spacca un nome in nome e cognome.
 *
 * ⚠️ La sorgente porta `firstName` e `surname` già separati per tutti i 2.826
 * soci: questa funzione è il RIPIEGO per quando uno dei due manca, non la
 * strada principale. Un cognome composto («De Luca») resta intero perché si
 * prende solo la PRIMA parola come nome.
 */
export function spaccaNome(intero) {
    const pezzi = String(intero ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (pezzi.length === 0)
        return { nome: '', cognome: '' };
    if (pezzi.length === 1)
        return { nome: pezzi[0], cognome: '' };
    return { nome: pezzi[0], cognome: pezzi.slice(1).join(' ') };
}
/**
 * ⭐⭐ **IL NOME ACCORCIATO PER IL CALENDARIO** — 17/09/2026, richiesta sua.
 *
 * 🗣️ *«possiamo introdurre la regola che mettiamo il nome e il cognome puntato
 *   [...] è il cognome puntato, non il nome. E direi di farlo quando lo spazio
 *   non basta»*.
 *
 * `Lidia` + `Comes` → **`Lidia C.`**
 *
 * ⚠️ **Gli è stato detto prima di farlo, e va ricordato qui**: puntando il
 * COGNOME si accorcia la metà che identifica di più — i nomi si ripetono molto
 * più dei cognomi. ⇒ Per questo esiste `accorciaElenco` qui sotto, che rimette
 * per esteso i nomi che si confonderebbero. Usare questa funzione da sola, su
 * un elenco, è il modo di reintrodurre il difetto.
 *
 * ⛔ Senza cognome non si inventa un puntino: un ospite registrato come
 * «Giovanni» resta «Giovanni». Un nome vuoto resta vuoto.
 */
export function nomeAccorciato(nome, cognome) {
    const n = nome.trim();
    const c = cognome.trim();
    if (n === '' || c === '')
        return n === '' ? c : n;
    // ⚠️ Il primo CARATTERE, non la prima lettera dell'ultima parola: «De Luca»
    // diventa «D.», che è quello che si scriverebbe a mano.
    return `${n} ${c[0]?.toUpperCase() ?? ''}.`;
}
/**
 * ⭐⭐ **ACCORCIA UN ELENCO, MA NON FINO A CONFONDERE DUE PERSONE.**
 *
 * ⚖️ È la guardia promessa quando si è scelto di puntare il cognome: due
 * «Marco A.» dentro la stessa prenotazione sono esattamente il difetto che
 * l'accorciatura doveva curare — un blocchetto che non dice chi c'è — solo
 * spostato di un passo.
 * ⇒ Chi si scontra torna per esteso; gli altri restano corti.
 *
 * 📌 *Non conta quante coppie ambigue esistano nel circolo: conta se capitano
 *    nello stesso riquadro. È lì che si guarda, ed è lì che si controlla.*
 *
 * ⚠️ Il confronto è su ciò che si VEDE (maiuscole e spazi normalizzati): due
 * scritte identiche a schermo sono ambigue anche se in colonna differiscono.
 */
export function accorciaElenco(voci) {
    const quanti = new Map();
    for (const v of voci) {
        const chiave = v.corto.trim().toLowerCase();
        quanti.set(chiave, (quanti.get(chiave) ?? 0) + 1);
    }
    return voci.map((v) => ((quanti.get(v.corto.trim().toLowerCase()) ?? 0) > 1 ? v.intero : v.corto));
}
/**
 * ⭐⭐ **CHI È GIÀ IN CAMPO, come insieme di identificativi.**
 *
 * 🗣️ Nasce il 17/09/2026 da una sua segnalazione: in una lezione con dentro
 * Laura Aprea e Marco Aprea, cercando «aprea» la scheda **li riproponeva tutti
 * e due**. Cliccarli non aggiungeva niente — il server risponde `409
 * SOCIO_GIA_IN_CAMPO` — ma la domanda era già stata fatta, e una domanda a cui
 * esiste una sola risposta sbagliata non andava fatta.
 *
 * ⚖️ **Gli OSPITI restano fuori di proposito.** Un ospite non è un'identità: è
 * un nome scritto a mano, e due persone possono chiamarsi uguale. Il server la
 * pensa allo stesso modo (`booking_players_socio_unico` è parziale, `where
 * member_id is not null`) ⇒ proibire due «Mario» sarebbe più severo della
 * regola vera, e si tratterebbe di una regola inventata qui.
 *
 * 🔒 Sta in `shared` perché i posti da guardare sono **due** e devono dire la
 * stessa cosa: la scheda di una prenotazione che esiste (i suoi `players`) e
 * quella di una che si sta creando (i posti ancora solo nel browser).
 */
export function sociGiaInCampo(posti) {
    return new Set(posti.map((p) => p.memberId).filter((x) => x !== null));
}
//# sourceMappingURL=soci.js.map